import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FAQSection } from '../components/FAQSection';
import { TRANSPORT_FAQS, CLEANING_FAQS } from '../lib/faqData';

const LOGO_URL = "/pt-logo.png?v=2";

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="faq-logo-link">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-14 w-auto" />
              <span className="font-bold text-xl text-slate-900 font-['Outfit']">Platinum Touch</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/transport">
                <Button variant="outline" className="text-sm font-medium" data-testid="faq-transport-link">
                  <Truck className="w-4 h-4 mr-1.5" /> Transport
                </Button>
              </Link>
              <Link to="/cleaning">
                <Button variant="outline" className="text-sm font-medium" data-testid="faq-cleaning-link">
                  <Sparkles className="w-4 h-4 mr-1.5" /> Cleaning
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-10 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Outfit'] tracking-tight mb-4">
            How Can We Help?
          </h1>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            Everything you need to know about our auto transport and car cleaning services
          </p>
        </div>
      </section>

      {/* Transport FAQs */}
      <div className="border-t border-slate-100">
        <FAQSection
          faqs={TRANSPORT_FAQS}
          accentColor="#38B6FF"
          title="Auto Transport"
          subtitle="Questions about our Lakewood, NJ to Orlando, FL vehicle transport service"
          sectionId="transport-faq"
        />
      </div>

      {/* Cleaning FAQs */}
      <div className="border-t border-slate-100 bg-slate-50">
        <FAQSection
          faqs={CLEANING_FAQS}
          accentColor="#10B981"
          title="Car Cleaning & Detailing"
          subtitle="Questions about our mobile car cleaning and detailing services"
          sectionId="cleaning-faq"
        />
      </div>

      {/* CTA */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-2xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#38B6FF]/20 flex items-center justify-center mb-4">
                  <Truck className="w-6 h-6 text-[#38B6FF]" />
                </div>
                <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Ready to Ship Your Car?</h3>
                <p className="text-slate-400 text-sm mb-6">Book your Pesach 2026 auto transport now</p>
              </div>
              <Link to="/booking">
                <Button className="bg-[#38B6FF] hover:bg-blue-500 text-white w-full font-semibold rounded-lg" data-testid="faq-book-transport-btn">
                  Book Transport <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">Need Your Car Cleaned?</h3>
                <p className="text-slate-400 text-sm mb-6">Professional mobile detailing at your door</p>
              </div>
              <Link to="/cleaning/booking">
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-white w-full font-semibold rounded-lg" data-testid="faq-book-cleaning-btn">
                  Book Cleaning <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} Platinum Touch. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
